import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaSave, 
  FaTimes,
  FaImage,
  FaEye,
  FaEyeSlash,
  FaTag,
  FaCalendarAlt,
  FaFolder,
  FaSlidersH,
  FaDesktop,
  FaMobileAlt,
  FaNewspaper,
  FaStar,
  FaList,
  FaChartBar,
  FaFilter,
  FaSync,
  FaYoutube
} from '../../../utils/iconUtils';
import { 
  getNews, 
  createNews, 
  updateNews, 
  deleteNews, 
  uploadNewsImage,
  getNewsCategories,
  getNewsForCarousel,
  getNewsForCards
} from '../../../firebase/firestoreService';
import { News } from '../../../types/news';
import { useAuth } from '../../../contexts/AuthContext';

const modules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'align': [] }],
    ['link', 'image'],
    ['clean']
  ],
};

const formats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'align',
  'link', 'image'
];

interface NewsFormData {
  title: string;
  content: string;
  excerpt: string;
  imageUrl: string;
  newsCardImageUrl: string;
  youtubeUrl: string;
  category: string;
  tags: string[];
  isPublished: boolean;
  showInCarousel: boolean;
  showInCards: boolean;
  showInGeneral: boolean;
  isUpcoming: boolean;
  otherImages: string[];
  newsCardImagePositionX: number;
  newsCardImagePositionY: number;
}

const NewsManagement: React.FC = () => {
  const auth = useAuth();
  const currentUser = auth.currentUser;
  const canViewNews = auth.canPerformAction('news', 'view');
  const canAddNews = auth.canPerformAction('news', 'create');
  const canEditNews = auth.canPerformAction('news', 'edit');
  const canDeleteNews = auth.canPerformAction('news', 'delete');
  const canPrintNews = auth.canPerformAction('news', 'print') || auth.canPerformAction('news', 'export');
  const canExportNews = auth.canPerformAction('news', 'export');
  const [news, setNews] = useState<News[]>([]);
  const [carouselNews, setCarouselNews] = useState<News[]>([]);
  const [cardsNews, setCardsNews] = useState<News[]>([]);
  const [loading, setLoading] = useState({
    all: true,
    carousel: true,
    cards: true,
    categories: true
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<NewsFormData>({
    title: '',
    content: '',
    excerpt: '',
    imageUrl: '',
    newsCardImageUrl: '',
    youtubeUrl: '',
    category: 'General',
    tags: [],
    isPublished: true,
    showInCarousel: false,
    showInCards: false,
    showInGeneral: true,
    isUpcoming: false,
    otherImages: [],
    newsCardImagePositionX: 50,
    newsCardImagePositionY: 50,
  });
  const [newTag, setNewTag] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingCardImage, setUploadingCardImage] = useState(false);
  const [uploadingOtherImages, setUploadingOtherImages] = useState(false);
  const DEFAULT_CATEGORIES = [
    'General',
    'Donation',
    'Program',
    'Counselling',
    'Health',
    'Community',
    'Education',
    'Events',
    'Volunteer',
    'Training',
    'Outreach',
    'Impact (Success Stories)',
    'Partnerships',
    'Announcements'
  ];
  const [categories, setCategories] = useState<string[]>(DEFAULT_CATEGORIES);
  const [activeView, setActiveView] = useState<'all' | 'carousel' | 'cards'>('all');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cardImageInputRef = useRef<HTMLInputElement>(null);
  const otherFileInputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [youtubePreview, setYoutubePreview] = useState<string>('');
  const MAX_OTHER_IMAGES = Infinity;
  const HOME_CAROUSEL_IMAGE_SIZE = '1560 x 525';

  // Function to extract YouTube video ID from URL
  const extractYoutubeId = (url: string): string => {
    if (!url) return '';
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7] && match[7].length === 11) ? match[7] : '';
  };

  // Function to validate YouTube URL
  const isValidYoutubeUrl = (url: string): boolean => {
    if (!url.trim()) return true; // Empty is valid (optional field)
    
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;
    if (!youtubeRegex.test(url)) {
      return false;
    }
    
    return extractYoutubeId(url).length === 11;
  };

  // Function to generate YouTube embed URL
  const getYoutubeEmbedUrl = (url: string): string => {
    const videoId = extractYoutubeId(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    // Update YouTube preview when youtubeUrl changes
    if (formData.youtubeUrl && isValidYoutubeUrl(formData.youtubeUrl)) {
      const embedUrl = getYoutubeEmbedUrl(formData.youtubeUrl);
      setYoutubePreview(embedUrl);
    } else {
      setYoutubePreview('');
    }
  }, [formData.youtubeUrl]);

  const fetchAllData = async () => {
    try {
      setLoading({ all: true, carousel: true, cards: true, categories: true });
      
      // Fetch all news
      const allNewsResult = await getNews({ limitCount: 100, onlyPublished: false });
      if (allNewsResult.success) {
        setNews(allNewsResult.data || []);
      }
      
      // Fetch carousel news
      const carouselResult = await getNewsForCarousel(50);
      if (carouselResult.success) {
        setCarouselNews(carouselResult.data || []);
      }
      
      // Fetch cards news
      const cardsResult = await getNewsForCards(50);
      if (cardsResult.success) {
        setCardsNews(cardsResult.data || []);
      }
      
      // Fetch categories and merge with defaults
      const categoriesResult = await getNewsCategories();
      if (categoriesResult.success && Array.isArray(categoriesResult.data)) {
        const merged = Array.from(new Set([...DEFAULT_CATEGORIES, ...categoriesResult.data]));
        setCategories(merged);
      } else {
        setCategories(DEFAULT_CATEGORIES);
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading({ all: false, carousel: false, cards: false, categories: false });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  

  const handleContentChange = (content: string) => {
    setFormData(prev => ({ 
      ...prev, 
      content,
      excerpt: prev.excerpt || content.substring(0, 150) + '...'
    }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.match('image.*')) {
      alert('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    try {
      setUploadingImage(true);
      const imageUrl = await uploadNewsImage(file);
      setFormData(prev => ({ ...prev, imageUrl }));
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCardImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.match('image.*')) {
      alert('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    try {
      setUploadingCardImage(true);
      // Use the same storage path as featured news images to match current storage rules.
      const newsCardImageUrl = await uploadNewsImage(file, 'news-images');
      setFormData(prev => ({ ...prev, newsCardImageUrl }));
    } catch (error) {
      console.error('Error uploading news card image:', error);
      alert('Failed to upload news card image');
    } finally {
      setUploadingCardImage(false);
      if (cardImageInputRef.current) {
        cardImageInputRef.current.value = '';
      }
    }
  };

  const handleOtherImagesUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const validFiles = files.filter(f => f.type.match('image.*'));
    if (validFiles.length === 0) {
      alert('Please upload valid image files');
      return;
    }

    try {
      setUploadingOtherImages(true);
      // enforce max other images
      const existingCount = (formData.otherImages || []).length;
      const availableSlots = Math.max(0, MAX_OTHER_IMAGES - existingCount);
      const toUpload = validFiles.slice(0, availableSlots);

      for (const file of toUpload) {
        if (file.size > 5 * 1024 * 1024) {
          console.warn('Skipping large image >5MB', file.name);
          continue;
        }
        const url = await uploadNewsImage(file);
        setFormData(prev => ({ ...prev, otherImages: [...(prev.otherImages || []), url] }));
      }
    } catch (error) {
      console.error('Error uploading other images:', error);
      alert('Failed to upload one or more images');
    } finally {
      setUploadingOtherImages(false);
      if (otherFileInputRef.current) otherFileInputRef.current.value = '';
    }
  };

  const handleRemoveOtherImage = (url: string) => {
    setFormData(prev => ({ ...prev, otherImages: (prev.otherImages || []).filter(i => i !== url) }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim() || !formData.imageUrl) {
      alert('Please fill in all required fields (title, content, and image)');
      return;
    }

    if (formData.youtubeUrl && !isValidYoutubeUrl(formData.youtubeUrl)) {
      alert('Please enter a valid YouTube URL. Supported formats:\n- https://youtube.com/watch?v=VIDEO_ID\n- https://youtu.be/VIDEO_ID\n- https://youtube.com/embed/VIDEO_ID');
      return;
    }

    if (!currentUser) {
      alert('You must be logged in to create news');
      return;
    }

    try {
      if (editingId) {
        await updateNews(editingId, formData);
        alert('News updated successfully!');
      } else {
        await createNews(formData, currentUser.uid);
        alert('News created successfully!');
      }
      
      resetForm();
      fetchAllData();
    } catch (error) {
      console.error('Error saving news:', error);
      alert('Failed to save news');
    }
  };

  const handleEdit = (newsItem: any) => {
    setEditingId(newsItem.id);
    setFormData({
      title: newsItem.title,
      content: newsItem.content,
      excerpt: newsItem.excerpt || '',
      imageUrl: newsItem.imageUrl,
      newsCardImageUrl: newsItem.newsCardImageUrl || '',
      youtubeUrl: newsItem.youtubeUrl || '',
      category: newsItem.category,
      tags: newsItem.tags || [],
      otherImages: newsItem.otherImages || [],
      isPublished: newsItem.isPublished,
      showInCarousel: newsItem.showInCarousel || false,
      showInCards: newsItem.showInCards || false,
      showInGeneral: newsItem.showInGeneral !== false,
      isUpcoming: newsItem.isUpcoming || false,
      newsCardImagePositionX: typeof newsItem.newsCardImagePositionX === 'number' ? newsItem.newsCardImagePositionX : 50,
      newsCardImagePositionY: typeof newsItem.newsCardImagePositionY === 'number' ? newsItem.newsCardImagePositionY : 50,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this news?')) {
      try {
        await deleteNews(id);
        fetchAllData();
        alert('News deleted successfully!');
      } catch (error) {
        console.error('Error deleting news:', error);
        alert('Failed to delete news');
      }
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setYoutubePreview('');
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      imageUrl: '',
      newsCardImageUrl: '',
      youtubeUrl: '',
      category: 'General',
      tags: [],
      otherImages: [],
      isPublished: true,
      showInCarousel: false,
      showInCards: false,
      showInGeneral: true,
      isUpcoming: false,
      newsCardImagePositionX: 50,
      newsCardImagePositionY: 50,
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getFilteredNews = () => {
    let filtered = news;

    if (activeView === 'carousel') {
      filtered = news.filter(item => item.showInCarousel);
    } else if (activeView === 'cards') {
      filtered = news.filter(item => item.showInCards);
    }

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        (item.title || '').toLowerCase().includes(q) ||
        (item.category || '').toLowerCase().includes(q) ||
        (item.tags || []).some((tag: string) => tag.toLowerCase().includes(q))
      );
    }

    return filtered;
  };

  const NewsBadge = ({ item }: { item: any }) => (
    <div className="flex flex-wrap gap-1 mt-2">
      {item.showInCarousel && (
        <span className="px-2 py-1 text-xs rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white flex items-center gap-1">
          <FaDesktop size={10} /> Carousel
        </span>
      )}
      {item.showInCards && (
        <span className="px-2 py-1 text-xs rounded-full bg-gradient-to-r from-violet-500 to-purple-500 text-white flex items-center gap-1">
          <FaMobileAlt size={10} /> Cards
        </span>
      )}
      {item.showInGeneral !== false && (
        <span className="px-2 py-1 text-xs rounded-full bg-gradient-to-r from-emerald-500 to-green-500 text-white flex items-center gap-1">
          <FaNewspaper size={10} /> General
        </span>
      )}
      {item.youtubeUrl && (
        <span className="px-2 py-1 text-xs rounded-full bg-gradient-to-r from-red-500 to-red-600 text-white flex items-center gap-1">
          <FaYoutube size={10} /> Video
        </span>
      )}
      {!item.showInCarousel && !item.showInCards && item.showInGeneral === false && !item.youtubeUrl && (
        <span className="px-2 py-1 text-xs rounded-full bg-gray-500 text-white">
          Hidden
        </span>
      )}
    </div>
  );

  const filteredNews = getFilteredNews();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">News Management Dashboard</h1>
          <p className="text-gray-600">Create, edit, and manage news visibility across different sections</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-800">
                    {editingId ? 'Edit News Article' : 'Create New News Article'}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {editingId ? 'Update the news article and its visibility settings' : 'Create a new article and set where it should appear'}
                  </p>
                </div>
                {editingId && (
                  <button
                    onClick={resetForm}
                    className="btn-outline text-sm flex items-center gap-2 hover:bg-gray-100"
                  >
                    <FaTimes />
                    Cancel Edit
                  </button>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <FaNewspaper className="text-primary-600" />
                    Basic Information
                  </h3>
                  
                  {/* Title */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter news title"
                      required
                    />
                  </div>

                  {/* Category and Published Status */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center cursor-pointer">
                        <div className="relative">
                          <input
                            type="checkbox"
                            checked={formData.isPublished}
                            onChange={(e) => setFormData(prev => ({ ...prev, isPublished: e.target.checked }))}
                            className="sr-only"
                          />
                          <div className={`block w-14 h-8 rounded-full ${formData.isPublished ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                          <div className={`absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${formData.isPublished ? 'transform translate-x-6' : ''}`}></div>
                        </div>
                        <span className="ml-3 text-sm font-medium text-gray-700">
                          {formData.isPublished ? 'Published' : 'Draft'}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* YouTube URL */}
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 border border-red-200">
                  <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                    <FaYoutube className="text-red-600" />
                    YouTube Video (Optional)
                  </h3>
                  <p className="text-sm text-red-600 mb-4">
                    Add a YouTube video URL to embed a video in the news article
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        YouTube URL
                      </label>
                      <input
                        type="text"
                        name="youtubeUrl"
                        value={formData.youtubeUrl}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="https://youtube.com/watch?v=..."
                      />
                      {formData.youtubeUrl && !isValidYoutubeUrl(formData.youtubeUrl) && (
                        <p className="mt-2 text-sm text-red-600">
                          Please enter a valid YouTube URL. Supported formats:
                          <br/>• https://youtube.com/watch?v=VIDEO_ID
                          <br/>• https://youtu.be/VIDEO_ID
                          <br/>• https://youtube.com/embed/VIDEO_ID
                        </p>
                      )}
                    </div>

                    {/* YouTube Preview */}
                    {youtubePreview && (
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                          <FaYoutube className="text-red-500" />
                          Video Preview
                        </h4>
                        <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-md">
                          <iframe
                            src={youtubePreview}
                            title="YouTube video preview"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="absolute top-0 left-0 w-full h-full"
                          />
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                          This is how the video will appear in the news article
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Visibility Settings */}
                <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-4 border border-primary-200">
                  <h3 className="font-semibold text-primary-800 mb-3 flex items-center gap-2">
                    <FaSlidersH className="text-primary-700" />
                    Visibility Settings
                  </h3>
                  <p className="text-sm text-primary-600 mb-4">
                    Control where this news article appears on the website
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Carousel Toggle */}
                    <div className="bg-white rounded-xl p-4 border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-lg">
                            <FaDesktop className="text-cyan-600" />
                          </div>
                          <span className="font-medium text-gray-800">Homepage Carousel</span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.showInCarousel}
                            onChange={(e) => setFormData(prev => ({ ...prev, showInCarousel: e.target.checked }))}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-cyan-500 peer-checked:to-blue-500"></div>
                        </label>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Appears in the main homepage carousel (recommended image: {HOME_CAROUSEL_IMAGE_SIZE})
                      </p>
                    </div>

                    {/* Cards Toggle */}
                    <div className="bg-white rounded-xl p-4 border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-gradient-to-br from-violet-100 to-purple-100 rounded-lg">
                            <FaMobileAlt className="text-violet-600" />
                          </div>
                          <span className="font-medium text-gray-800">News Cards</span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.showInCards}
                            onChange={(e) => setFormData(prev => ({ ...prev, showInCards: e.target.checked }))}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-violet-500 peer-checked:to-purple-500"></div>
                        </label>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Appears in homepage news cards (recommended image: 1:1 square)
                      </p>
                    </div>

                    {/* General News Toggle */}
                    <div className="bg-white rounded-xl p-4 border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-gradient-to-br from-emerald-100 to-green-100 rounded-lg">
                            <FaNewspaper className="text-emerald-600" />
                          </div>
                          <span className="font-medium text-gray-800">General News</span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.showInGeneral}
                            onChange={(e) => setFormData(prev => ({ ...prev, showInGeneral: e.target.checked }))}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-emerald-500 peer-checked:to-green-500"></div>
                        </label>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Appears in general news listings and archives
                      </p>
                    </div>

                    {/* Upcoming Toggle (only for Program category) */}
                    <div className="bg-white rounded-xl p-4 border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-gradient-to-br from-amber-100 to-amber-200 rounded-lg">
                            <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.176 0l-3.37 2.448c-.784.57-1.839-.197-1.54-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.063 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69L9.05 2.927z"/></svg>
                          </div>
                          <span className="font-medium text-gray-800">Mark as Upcoming</span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.isUpcoming}
                            onChange={(e) => setFormData(prev => ({ ...prev, isUpcoming: e.target.checked }))}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-amber-400 peer-checked:to-amber-600"></div>
                        </label>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Mark this program as an upcoming event. A banner will appear in the homepage carousel when shown.
                      </p>
                      
                    </div>
                  </div>
                </div>

                {/* Image Upload */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <FaImage className="text-primary-600" />
                    Featured Image *
                  </h3>
                  <div className="space-y-4">
                    {formData.imageUrl ? (
                      <div className="space-y-4">
                        <div className="relative">
                          <img
                            src={formData.imageUrl}
                            alt="Preview"
                            className="w-full max-h-80 object-contain rounded-xl border border-gray-200 bg-white"
                          />
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, imageUrl: '' }))}
                            className="absolute top-3 right-3 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                          >
                            <FaTimes />
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-white rounded-xl border border-gray-200 p-3">
                            <p className="text-xs font-semibold text-gray-700 mb-2">
                              Homepage Carousel Preview ({HOME_CAROUSEL_IMAGE_SIZE})
                            </p>
                            <div
                              className="w-full rounded-lg overflow-hidden border border-gray-200 bg-gray-100"
                              style={{ aspectRatio: '1560 / 525' }}
                            >
                              <img
                                src={formData.imageUrl}
                                alt="Carousel preview"
                                className="w-full h-full object-cover object-center"
                              />
                            </div>
                          </div>

                          <div className="bg-white rounded-xl border border-gray-200 p-3">
                            <p className="text-xs font-semibold text-gray-700 mb-2">
                              Homepage News Card Preview (Square 1:1)
                            </p>
                            <div className="w-full max-w-[260px] rounded-lg overflow-hidden border border-gray-200 bg-gray-100 aspect-square">
                              <img
                                src={formData.newsCardImageUrl || formData.imageUrl}
                                alt="Card preview"
                                className="w-full h-full object-cover"
                                style={{ objectPosition: `${formData.newsCardImagePositionX}% ${formData.newsCardImagePositionY}%` }}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="bg-white rounded-xl border border-gray-200 p-4">
                          <p className="text-sm font-semibold text-gray-800 mb-3">
                            News Card Crop Position (Only affects homepage news cards)
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                <span>Horizontal Focus</span>
                                <span>{Math.round(formData.newsCardImagePositionX)}%</span>
                              </div>
                              <input
                                type="range"
                                min={0}
                                max={100}
                                value={formData.newsCardImagePositionX}
                                onChange={(e) => setFormData(prev => ({ ...prev, newsCardImagePositionX: Number(e.target.value) }))}
                                className="w-full accent-primary-600"
                              />
                            </div>
                            <div>
                              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                <span>Vertical Focus</span>
                                <span>{Math.round(formData.newsCardImagePositionY)}%</span>
                              </div>
                              <input
                                type="range"
                                min={0}
                                max={100}
                                value={formData.newsCardImagePositionY}
                                onChange={(e) => setFormData(prev => ({ ...prev, newsCardImagePositionY: Number(e.target.value) }))}
                                className="w-full accent-primary-600"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="bg-white rounded-xl border border-gray-200 p-4">
                          <p className="text-sm font-semibold text-gray-800 mb-2">
                            Card-Specific Image (Optional)
                          </p>
                          <p className="text-xs text-gray-600 mb-3">
                            If set, homepage news cards use this image only. Carousel and details keep the featured image.
                          </p>
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={() => cardImageInputRef.current?.click()}
                              className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
                            >
                              Upload Card Image
                            </button>
                            <input
                              ref={cardImageInputRef}
                              type="file"
                              accept="image/*"
                              onChange={handleCardImageUpload}
                              className="hidden"
                            />
                            {formData.newsCardImageUrl && (
                              <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, newsCardImageUrl: '' }))}
                                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                              >
                                Remove Card Image
                              </button>
                            )}
                            {uploadingCardImage && (
                              <span className="text-sm text-gray-600">Uploading card image...</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-primary-500 transition-colors bg-white"
                      >
                        <FaImage className="text-4xl text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600 mb-2">Click to upload image</p>
                        <p className="text-sm text-gray-500">PNG, JPG, GIF up to 5MB</p>
                        <p className="text-xs text-gray-500 mt-2">
                          Home carousel: {HOME_CAROUSEL_IMAGE_SIZE} landscape. News cards: 1:1 square.
                        </p>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                      </div>
                    )}
                    {uploadingImage && (
                      <div className="text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                        <p className="mt-2 text-gray-600">Uploading image...</p>
                      </div>
                    )}
                    {/* Other Images (Gallery) */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Other Images (Gallery)</h4>
                      <div className="flex items-center gap-2 mb-3">
                        <button
                          type="button"
                          onClick={() => otherFileInputRef.current?.click()}
                          className="px-3 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 text-sm flex items-center gap-2"
                          disabled={(formData.otherImages || []).length >= MAX_OTHER_IMAGES}
                        >
                          <FaPlus />
                          Add More Images
                        </button>
                        <div className="text-sm text-gray-600">{(formData.otherImages || []).length} added</div>
                        {uploadingOtherImages && (
                          <div className="text-sm text-gray-600">Uploading...</div>
                        )}
                      </div>

                      <input
                        ref={otherFileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleOtherImagesUpload}
                        className="hidden"
                      />

                      {(formData.otherImages || []).length === 0 ? (
                        <p className="text-sm text-gray-500">No additional images added.</p>
                      ) : (
                        <div className="grid grid-cols-3 gap-3">
                          {formData.otherImages.map((url, idx) => (
                            <div key={url + idx} className="relative rounded overflow-hidden border border-gray-200">
                              <img src={url} alt={`other-${idx}`} className="w-full h-24 object-cover" />
                              <button
                                type="button"
                                onClick={() => handleRemoveOtherImage(url)}
                                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600"
                                title="Remove image"
                              >
                                <FaTrash size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Content Editor */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <FaEdit className="text-primary-600" />
                    Content *
                  </h3>
                  <ReactQuill
                    theme="snow"
                    value={formData.content}
                    onChange={handleContentChange}
                    modules={modules}
                    formats={formats}
                    className="h-64 mb-12 bg-white rounded-lg"
                  />
                </div>

                {/* Excerpt */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-700 mb-3">
                    Excerpt (Brief Description)
                  </h3>
                  <textarea
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Brief description of the news"
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    {formData.excerpt.length}/200 characters
                  </p>
                </div>

                {/* Tags */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-700 mb-3">
                    Tags
                  </h3>
                  <div className="flex items-center mb-2">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                      className="flex-grow px-4 py-3 border border-gray-300 rounded-l-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Add a tag"
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="bg-primary-600 text-white px-4 py-3 rounded-r-xl hover:bg-primary-700"
                    >
                      <FaPlus />
                    </button>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.tags.map(tag => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-primary-100 text-primary-800"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-2 text-primary-600 hover:text-primary-800"
                        >
                          <FaTimes size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4 border-t">
                  {(editingId ? canEditNews : canAddNews) && (
                    <button
                      type="submit"
                      disabled={uploadingImage || uploadingCardImage}
                      className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-3 px-4 rounded-xl hover:from-primary-700 hover:to-primary-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg hover:shadow-xl transition-all"
                    >
                      <FaSave className="inline mr-2" />
                      {editingId ? 'Update News Article' : 'Publish News Article'}
                    </button>
                  )}
                </div>
              </form>
            </motion.div>
          </div>

          {/* News List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 h-full">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">News Articles</h2>
                
                {/* Search and Filter */}
                <div className="space-y-4">
                  <div className="relative">
                    <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search news..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  
                  {/* View Toggles */}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setActiveView('all')}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        activeView === 'all'
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <FaList className="inline mr-2" />
                      All News ({news.length})
                    </button>
                    <button
                      onClick={() => setActiveView('carousel')}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        activeView === 'carousel'
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <FaDesktop className="inline mr-2" />
                      Carousel ({carouselNews.length})
                    </button>
                    <button
                      onClick={() => setActiveView('cards')}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        activeView === 'cards'
                          ? 'bg-gradient-to-r from-violet-500 to-purple-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <FaMobileAlt className="inline mr-2" />
                      Cards ({cardsNews.length})
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-4 gap-2 mb-6">
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <div className="text-lg font-bold text-gray-800">{news.length}</div>
                  <div className="text-xs text-gray-500">Total</div>
                </div>
                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-3 text-center">
                  <div className="text-lg font-bold text-cyan-700">{carouselNews.length}</div>
                  <div className="text-xs text-cyan-600">Carousel</div>
                </div>
                <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-3 text-center">
                  <div className="text-lg font-bold text-violet-700">{cardsNews.length}</div>
                  <div className="text-xs text-violet-600">Cards</div>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-3 text-center">
                  <div className="text-lg font-bold text-red-700">
                    {news.filter(item => item.youtubeUrl).length}
                  </div>
                  <div className="text-xs text-red-600">With Video</div>
                </div>
              </div>
              
              {loading.all ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  <p className="mt-2 text-gray-600">Loading news...</p>
                </div>
              ) : filteredNews.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FaNewspaper className="text-4xl mx-auto mb-4 text-gray-300" />
                  <p>No news articles found</p>
                  <p className="text-sm mt-1">Create your first news article</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
                  {filteredNews.map((item) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow bg-white"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden">
                          <img
                            src={item.imageUrl}
                            alt={item.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        
                        <div className="flex-grow min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              item.isPublished 
                                ? 'bg-green-100 text-green-800 border border-green-200'
                                : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                            }`}>
                              {item.isPublished ? 'Published' : 'Draft'}
                            </span>
                            <div className="flex items-center space-x-2">
                              {item.youtubeUrl && (
                                <span className="text-red-500" title="Has YouTube video">
                                  <FaYoutube size={14} />
                                </span>
                              )}
                              {canEditNews && (
                                <button
                                  onClick={() => handleEdit(item)}
                                  className="text-primary-600 hover:text-primary-800 p-1 hover:bg-primary-50 rounded"
                                  title="Edit"
                                >
                                  <FaEdit size={14} />
                                </button>
                              )}
                              {canDeleteNews && (
                                <button
                                  onClick={() => handleDelete(item.id)}
                                  className="text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded"
                                  title="Delete"
                                >
                                  <FaTrash size={14} />
                                </button>
                              )}
                            </div>
                          </div>
                          
                          <h3 className="font-medium text-gray-900 truncate text-sm">
                            {item.title}
                          </h3>
                          
                          <div className="flex items-center text-xs text-gray-500 mt-1">
                            <FaFolder className="mr-1" size={10} />
                            <span className="mr-3">{item.category}</span>
                            <FaCalendarAlt className="mr-1" size={10} />
                            <span>{formatDate(item.date)}</span>
                          </div>
                          
                          <div className="flex items-center text-xs text-gray-500 mt-1">
                            <FaEye className="mr-1" size={10} />
                            <span>{item.views || 0} views</span>
                          </div>
                          
                          <NewsBadge item={item} />
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
              
              {/* Refresh Button */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={fetchAllData}
                  className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <FaSync className={`${loading.all ? 'animate-spin' : ''}`} />
                  Refresh List
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsManagement;
